// apps/web/src/pages/author/sections/OptionCategories.tsx
//
// Phase 8 Plan 08 Task 3 — OptionCategories form section (authr-01 /
// authr-02 / authr-03).
//
// authr-02 contract: when an OptionCategory carries a visibleWhen predicate,
// the ConditionalCategoryRow reads the source widget's state via per-path
// useWatch, then calls `isVisible(predicate, sourceState)` from the lifted
// shared evaluator (@the-recipe-spread/shared). When the predicate evaluates
// false, the row returns `null` (DOM hard-remove per CONTEXT D-04 retain-
// value contract — RHF shouldUnregister defaults to false so the underlying
// form-state value survives the hide cycle for re-show).
//
// SCHEMA NOTE: the OptionCategory schema (packages/shared/src/schemas/
// option-category.ts line 191-205) uses `label` for the category name field
// — UI-SPEC §"Destructive confirmations" line 373 writes `{category.name}`,
// but the schema is the source of truth (same W13-style reconciliation as
// IngredientGroup). The Delete Dialog body interpolates `{category.label}`
// accordingly.
//
// SCHEMA NOTE 2: OptionCategory.blocks.min(1).max(20) at the schema level.
// Phase 8 ships an EMPTY blocks default (Phase 9 implements the block-
// discrimination editor). New rows therefore violate the schema until the
// author fills them in — that's expected: zod-resolver validation fires at
// save, not on every render. The form-state holds whatever the author types.
//
// PREDICATE DSL: Phase 8 ships the Phase 6 minimal predicate shape
// (VisibleWhen = { sourceWidgetId, anyOf: string[] }). Phase 9 (D-15)
// generalizes this into the full visibility Constraint AST; the
// `isVisible` evaluator signature stays stable across that migration.
//
// authr-06 hygiene: per-path useWatch only; no formState reads.

import { isVisible, type VisibleWhen } from "@the-recipe-spread/shared";
import { useId } from "react";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FieldArrayRow } from "../FieldArrayRow";

// Default values for a brand-new option category. blocks: [] violates the
// schema (.min(1)) — Phase 9 lands the blocks editor; until then, the
// FormDescription points authors at the Phase 9 timeline.
function newCategoryDefaults(): { id: string; label: string; blocks: never[] } {
  return { id: crypto.randomUUID(), label: "", blocks: [] };
}

export function OptionCategories(): React.ReactElement {
  const { control } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "optionCategories",
  });

  return (
    <Fieldset legend="OPTION CATEGORIES">
      {fields.length === 0 ? (
        <EmptyState
          title="No option categories yet."
          cta={{
            label: "Add option category",
            onClick: () => append(newCategoryDefaults()),
          }}
        />
      ) : (
        <>
          {fields.map((field, index) => (
            <ConditionalCategoryRow
              key={field.id}
              index={index}
              canMoveUp={index > 0}
              canMoveDown={index < fields.length - 1}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
              onRemove={() => remove(index)}
            />
          ))}
          <button
            type="button"
            onClick={() => append(newCategoryDefaults())}
            className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Add option category
          </button>
        </>
      )}
    </Fieldset>
  );
}
OptionCategories.displayName = "OptionCategories";

interface ConditionalCategoryRowProps {
  index: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

// authr-02 — per RESEARCH §"Pattern 5": the conditional row subscribes to
// the form's widgets[] array (per-path on a single top-level field), finds
// the source widget by instanceId, and reads its state. The lookup runs
// inside the evaluator because the form-state holds widgets as an ordered
// array (widgets[i]) while the predicate references widgets by instanceId.
// When the predicate is undefined or the source widget can't be found, the
// row falls back to isVisible's hide-by-default behavior. When isVisible
// returns false, the row returns null (DOM hard-remove per CONTEXT D-04).
//
// Subscription scope: useWatch({ name: 'widgets' }) IS per-path — it
// subscribes ONLY to the widgets array, not to the entire form. Mutations
// to title / instructions / ingredientGroups do NOT cause this row to
// re-render. The price is one re-render per widget-array mutation, which
// is acceptable for Phase 8 (Phase 9's WidgetInstances editor will run on
// a different cadence than per-keystroke).
function ConditionalCategoryRow({
  index,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
}: ConditionalCategoryRowProps): React.ReactElement | null {
  const { control } = useFormContext();
  const visibleWhen = useWatch({
    control,
    name: `optionCategories.${index}.visibleWhen`,
  }) as VisibleWhen | undefined;
  // Per-path: subscribe to the widgets array as a single unit. The hot
  // sub-tree (CategoryEditor) does NOT take this subscription — it lives in
  // a child component below.
  const widgets = useWatch({ control, name: "widgets" }) as
    | Array<{ instanceId?: string; state?: unknown }>
    | undefined;
  let sourceState: ({ selected?: unknown } & { readonly [key: string]: unknown }) | undefined;
  if (visibleWhen && Array.isArray(widgets)) {
    const found = widgets.find((w) => w?.instanceId === visibleWhen.sourceWidgetId);
    sourceState =
      found && typeof found.state === "object" && found.state !== null
        ? (found.state as { selected?: unknown } & { readonly [key: string]: unknown })
        : undefined;
  }

  if (!isVisible(visibleWhen, sourceState)) {
    return null;
  }

  return (
    <CategoryEditor
      index={index}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onRemove={onRemove}
    />
  );
}

interface CategoryEditorProps {
  index: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function CategoryEditor({
  index,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
}: CategoryEditorProps): React.ReactElement {
  const { control, setValue, getValues } = useFormContext();

  // Per-path label read for the Fieldset legend + Delete Dialog body.
  // Note: OptionCategory schema uses `label`, not `name` — UI-SPEC line 373
  // wording (`{category.name}`) does NOT match the schema field. We
  // interpolate `{category.label}` (schema is source of truth).
  const label = useWatch({ control, name: `optionCategories.${index}.label` });
  const labelText = typeof label === "string" && label.length > 0 ? label : `Category ${index + 1}`;

  // visibleWhen toggle: when checked, the row carries a predicate object;
  // when unchecked, predicate is undefined.
  const visibleWhen = useWatch({
    control,
    name: `optionCategories.${index}.visibleWhen`,
  }) as VisibleWhen | undefined;
  const hasPredicate = visibleWhen !== undefined;

  // anyOf CSV ↔ string[] round-trip (same shape as Constraints section).
  const anyOfArray = visibleWhen?.anyOf;
  const anyOfText = Array.isArray(anyOfArray) ? anyOfArray.join(", ") : "";

  // CR-02 fix — togglePredicate populates the visibleWhen subtree ONLY when
  // currently undefined. Re-enabling a previously-disabled predicate
  // preserves whatever sourceWidgetId/anyOf the author had typed before;
  // UI-SPEC's "Hidden categories retain their values until shown again"
  // contract now applies to the toggle path as well as the visibility path.
  const togglePredicate = (next: boolean): void => {
    if (next) {
      const current = getValues(`optionCategories.${index}.visibleWhen`);
      if (current === undefined) {
        setValue(
          `optionCategories.${index}.visibleWhen`,
          { sourceWidgetId: "", anyOf: [] },
          { shouldDirty: true },
        );
      }
      // Already populated → no-op; preserve previously-typed values.
    } else {
      setValue(`optionCategories.${index}.visibleWhen`, undefined, { shouldDirty: true });
    }
  };

  // CR-02 fix — handleAnyOfBlur reads the LIVE visibleWhen.sourceWidgetId
  // from form state (`getValues`) at blur time. The sourceWidgetId path is
  // now a Controller-bound input, so its current value lives in form state
  // even mid-typing — no race between the source-widget keystroke handler
  // and the anyOf blur handler.
  const handleAnyOfBlur = (raw: string): void => {
    const parsed = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const current = getValues(`optionCategories.${index}.visibleWhen`) as VisibleWhen | undefined;
    setValue(
      `optionCategories.${index}.visibleWhen`,
      { sourceWidgetId: current?.sourceWidgetId ?? "", anyOf: parsed },
      { shouldDirty: true },
    );
  };

  const toggleId = useId();
  const sourceFieldId = useId();
  const anyOfFieldId = useId();

  return (
    <FieldArrayRow
      singular="option category"
      index={index}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onRemove={onRemove}
      deleteDialogTitle="Delete option category?"
      // schema is source of truth — label not name (W13-style reconciliation
      // applies here too: UI-SPEC line 373 writes {category.name} but schema
      // uses `label`).
      deleteDialogBody={`${labelText} will be removed. Widgets that reference its options will need to be reconfigured.`}
      deleteConfirmLabel="Delete category"
    >
      <Fieldset legend={labelText}>
        <FormField
          control={control}
          name={`optionCategories.${index}.label`}
          render={({ field }) => (
            <FormItem data-field-path={`optionCategories.${index}.label`}>
              <FormLabel>Label</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={typeof field.value === "string" ? field.value : ""}
                  autoFocus={!field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <p className="text-sm text-muted-foreground">
          Blocks are managed in the spread composer (Phase 9). v1 starts with an empty blocks list.
        </p>

        {/* visibleWhen toggle + sub-form. */}
        <div className="flex items-center gap-2">
          <Checkbox
            id={toggleId}
            checked={hasPredicate}
            onCheckedChange={(checked) => togglePredicate(checked === true)}
          />
          <Label htmlFor={toggleId}>Conditional visibility (visibleWhen)</Label>
        </div>

        {hasPredicate ? (
          <>
            <div
              className="space-y-2"
              data-field-path={`optionCategories.${index}.visibleWhen.sourceWidgetId`}
            >
              <Label htmlFor={sourceFieldId}>Source widget id</Label>
              {/* CR-02 fix — Controller-bound so each keystroke writes ONLY to
                  visibleWhen.sourceWidgetId via RHF's native subfield path.
                  The previous setValue-on-change pattern wrote the entire
                  visibleWhen object on every keystroke, reading stale anyOf
                  via getValues and clobbering in-flight Textarea changes. */}
              <Controller
                control={control}
                name={`optionCategories.${index}.visibleWhen.sourceWidgetId`}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={sourceFieldId}
                    value={typeof field.value === "string" ? field.value : ""}
                  />
                )}
              />
              <p className="text-sm text-muted-foreground">
                Pick the widget whose state controls this rule.
              </p>
            </div>

            <div
              className="space-y-2"
              data-field-path={`optionCategories.${index}.visibleWhen.anyOf`}
            >
              <Label htmlFor={anyOfFieldId}>Any of (option ids)</Label>
              <Textarea
                id={anyOfFieldId}
                defaultValue={anyOfText}
                key={anyOfText}
                onBlur={(e) => handleAnyOfBlur(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                {/* WR-11 — make the kebab-case constraint visible at the
                    input site so authors don't get a cryptic 422 on save
                    when they type entries with spaces. */}
                Comma-separated option ids (kebab-case: lowercase, digits, hyphens). When any is
                selected in the source widget, this category becomes visible.
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              Hidden categories retain their values until shown again.
            </p>
          </>
        ) : null}
      </Fieldset>
    </FieldArrayRow>
  );
}
